// API configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api/v1';

/**
 * Fetch planning activities from the API
 * 
 * @param {Object} params - Query parameters
 * @param {string|Date} [params.date] - Filter by specific date (ISO format or YYYY-MM-DD)
 * @param {string} [params.equipment_id] - Filter by specific equipment ID
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   activities: Array<{
 *     id: number,
 *     equipment_id: string,
 *     equipment_name: string,
 *     activity_type: 'manufacturing' | 'cleaning' | 'maintenance',
 *     batch_number: string | null,
 *     cleaning_type: string | null,
 *     start_time: string,
 *     end_time: string
 *   }>,
 *   meta: {
 *     display_start_time: string,
 *     display_end_time: string,
 *     total_activities: number
 *   }
 * }>}
 */
export const fetchPlanning = async ({ date = null, equipment_id = null } = {}) => {
  try {
    // const url = new URL(`${API_BASE_URL}/equipment-planner/planning?date=2025-05-08`); 
    // Use current date if no date is provided
    const currentDate = date || new Date();
    const dateStr = currentDate instanceof Date ? currentDate.toISOString().split('T')[0] : currentDate;
    
    const url = new URL(`${API_BASE_URL}/equipment-planner/planning`);
    url.searchParams.append('date', dateStr);
    
    if (equipment_id) {
      url.searchParams.append('equipment_id', equipment_id);
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching planning data:', error);
    throw error;
  }
};

/**
 * Fetch equipment list from the API
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   data: Array<{
 *     id: string,
 *     equipment_name: string
 *   }>
 * }>}
 */
export const fetchEquipmentList = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/equipment-list`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching equipment list:', error);
    throw error;
  }
};

/**
 * Fetch area list from the API (used for Physical Log Entry)
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     area_name: string,
 *     area_id: string
 *   }>
 * }>}
 */
export const fetchAreaList = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/get-area-list`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching area list:', error);
    throw error;
  }
};

/**
 * Fetch equipment IDs from logs
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   data: string[]
 * }>}
 */
export const fetchEquipmentFromLogs = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/equipment-from-logs`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching equipment from logs:', error);
    throw error;
  }
};

/**
 * Fetch activity details by ID
 * 
 * @param {number|string} activityId - The activity ID
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   data: {
 *     id: number,
 *     eqp_log_type: string,
 *     equipment_id: string,
 *     product_batch: string,
 *     start_date: string,
 *     end_date: string
 *   }
 * }>}
 */
export const fetchActivityDetails = async (activityId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/activity/${activityId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching activity details:', error);
    throw error;
  }
};

/**
 * Fetch equipment utilization summary
 * 
 * @param {Object} params - Query parameters
 * @param {string|Date} params.start_date - Start date (required)
 * @param {string|Date} params.end_date - End date (required)
 * @param {string} [params.equipment_id] - Optional equipment ID filter
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   start_date: string,
 *   end_date: string,
 *   data: Array<{
 *     equipment_id: string,
 *     eqp_log_type: string,
 *     count: string,
 *     total_hours: string
 *   }>
 * }>}
 */
export const fetchUtilization = async ({ start_date, end_date, equipment_id = null }) => {
  try {
    const url = new URL(`${API_BASE_URL}/equipment-planner/utilization`);
    
    const startDateStr = start_date instanceof Date ? start_date.toISOString().split('T')[0] : start_date;
    const endDateStr = end_date instanceof Date ? end_date.toISOString().split('T')[0] : end_date;
    
    url.searchParams.append('start_date', startDateStr);
    url.searchParams.append('end_date', endDateStr);
    
    if (equipment_id) {
      url.searchParams.append('equipment_id', equipment_id);
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching utilization data:', error);
    throw error;
  }
};

/**
 * Fetch available plan versions
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   data: Array<{
 *     plan_version: number,
 *     published_at: string,
 *     record_count: string
 *   }>,
 *   count: number
 * }>}
 */
export const fetchPlanVersions = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/plan-versions`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching plan versions:', error);
    throw error;
  }
};

/**
 * Fetch available products for equipment planner
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   data: Array<{
 *     label: string,
 *     value: string
 *   }>,
 *   count: number
 * }>}
 */
export const fetchAvailableProducts = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/available-products`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching available products:', error);
    throw error;
  }
};

/**
 * Fetch product volume options for a specific product code
 * 
 * @param {string} productCode - The product code to fetch options for
 * @returns {Promise<{
 *   success: boolean,
 *   data: {
 *     product_code: string,
 *     options: Array<{
 *       id: number,
 *       label: string,
 *       value: string,
 *       num_batches: string,
 *       volume_gran_fbp_blend: string,
 *       volume_comp_coat_cap_imp: string
 *     }>
 *   }
 * }>}
 */
export const fetchProductVolumeOptions = async (productCode) => {
  try {
    const url = new URL(`${API_BASE_URL}/equipment-planner/product-volume-options`);
    url.searchParams.append('product_code', productCode);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching product volume options:', error);
    throw error;
  }
};

/**
 * Fetch activities grouped by location/equipment
 * 
 * @param {Object} params - Query parameters
 * @param {string|Date} [params.start_date] - Start date/time for filtering
 * @param {string|Date} [params.end_date] - End date/time for filtering
 * @param {number|null} [params.plan_version] - Specific plan version to use (null for latest)
 * 
 * @returns {Promise<Object>} Activities grouped by equipment name
 */
export const fetchActivitiesByLocation = async ({ start_date = null, end_date = null, plan_version = null } = {}) => {
  try {
    const url = new URL(`${API_BASE_URL}/equipment-planner/activities-by-location`);
    
    if (start_date) {
      const startDateStr = start_date instanceof Date 
        ? start_date.toISOString().replace('T', ' ').substring(0, 16)
        : start_date;
      url.searchParams.append('start_date', startDateStr);
    }
    
    if (end_date) {
      const endDateStr = end_date instanceof Date 
        ? end_date.toISOString().replace('T', ' ').substring(0, 16)
        : end_date;
      url.searchParams.append('end_date', endDateStr);
    }
    
    // Only add plan_version if a specific version is selected (not null/latest)
    if (plan_version !== null && plan_version !== undefined) {
      url.searchParams.append('plan_version', plan_version);
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    return data;
  } catch (error) {
    console.error('Error fetching activities by location:', error);
    throw error;
  }
};

/**
 * Fetch activities grouped by location/equipment (Physical Log)
 * 
 * @param {Object} params - Query parameters
 * @param {string|Date} [params.start_date] - Start date/time for filtering
 * @param {string|Date} [params.end_date] - End date/time for filtering
 * @param {number|null} [params.plan_version] - Specific plan version to use (null for latest)
 * 
 * @returns {Promise<Object>} Activities grouped by equipment name
 */
export const fetchActivitiesByLocationPhysical = async ({ start_date = null, end_date = null, plan_version = null } = {}) => {
  try {
    const url = new URL(`${API_BASE_URL}/equipment-planner/activities-by-location-physical`);
    
    if (start_date) {
      const startDateStr = start_date instanceof Date 
        ? start_date.toISOString().replace('T', ' ').substring(0, 16)
        : start_date;
      url.searchParams.append('start_date', startDateStr);
    }
    
    if (end_date) {
      const endDateStr = end_date instanceof Date 
        ? end_date.toISOString().replace('T', ' ').substring(0, 16)
        : end_date;
      url.searchParams.append('end_date', endDateStr);
    }
    
    // Only add plan_version if a specific version is selected (not null/latest)
    if (plan_version !== null && plan_version !== undefined) {
      url.searchParams.append('plan_version', plan_version);
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    return data;
  } catch (error) {
    console.error('Error fetching activities by location (physical):', error);
    throw error;
  }
};

/**
 * Fetch activities CSV for export to Excel
 * 
 * @param {Object} params - Query parameters
 * @param {string|Date} [params.start_date] - Start date/time for filtering (format: YYYY-MM-DD HH:mm:ss)
 * @param {string|Date} [params.end_date] - End date/time for filtering (format: YYYY-MM-DD HH:mm:ss)
 * @param {number|null} [params.plan_version] - Specific plan version to use (null for latest)
 * 
 * @returns {Promise<Blob>} CSV file blob for download
 */
export const fetchActivitiesByLocationCSV = async ({ start_date = null, end_date = null, plan_version = null } = {}) => {
  try {
    const url = new URL(`${API_BASE_URL}/equipment-planner/activities-by-location-csv`);
    
    if (start_date) {
      const startDateStr = start_date instanceof Date 
        ? start_date.toISOString().replace('T', ' ').substring(0, 19)
        : start_date;
      url.searchParams.append('start_date', startDateStr);
    }
    
    if (end_date) {
      const endDateStr = end_date instanceof Date 
        ? end_date.toISOString().replace('T', ' ').substring(0, 19)
        : end_date;
      url.searchParams.append('end_date', endDateStr);
    }
    
    // Only add plan_version if a specific version is selected (not null/latest)
    if (plan_version !== null && plan_version !== undefined) {
      url.searchParams.append('plan_version', plan_version);
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Get the blob from the response
    const blob = await response.blob();
    return blob;
  } catch (error) {
    console.error('Error fetching activities CSV:', error);
    throw error;
  }
};

/**
 * Fetch plan vs actual summary data
 * 
 * @param {Object} params - Query parameters
 * @param {string} params.start_date - Start date for filtering (format: YYYY-MM-DD)
 * @param {string} params.end_date - End date for filtering (format: YYYY-MM-DD)
 * @param {number|null} [params.plan_version] - Specific plan version to use (null for latest)
 * 
 * @returns {Promise<Object>} Plan vs actual summary data
 */
export const fetchPlanVsActualSummary = async ({ start_date, end_date, plan_version = null } = {}) => {
  try {
    const url = new URL(`${API_BASE_URL}/equipment-planner/plan-vs-actual-summary`);
    
    if (start_date) {
      url.searchParams.append('start_date', start_date);
    }
    
    if (end_date) {
      url.searchParams.append('end_date', end_date);
    }
    
    // Only add plan_version if a specific version is selected (not null/latest)
    if (plan_version !== null && plan_version !== undefined) {
      url.searchParams.append('plan_version', plan_version);
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching plan vs actual summary:', error);
    throw error;
  }
};

/**
 * Search for product batch information
 * 
 * @param {string} search - Batch number to search for
 * @returns {Promise<Object>} - Product batch information
 */
export const searchProductBatch = async (search) => {
  try {
    // const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://nucleus-staging-api.gravitipharma.com/api/v1';
    const url = `${API_BASE_URL}/equipment-planner/search-batch?search=${encodeURIComponent(search)}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error searching product batch:', error);
    throw error;
  }
};

/**
 * Fetch equipment planner data for a specific date
 * 
 * @param {string|Date} date - Date in format YYYY-MM-DD or Date object
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   data?: Array<{
 *     id: number,
 *     equipment_id: string,
 *     equipment_name: string,
 *     activity_type: string,
 *     batch_number?: string,
 *     cleaning_type?: string,
 *     maintenance_type?: string,
 *     start_date: string,
 *     end_date: string
 *   }>
 * }>}
 */
export const fetchEqpPlanner = async (date) => {
  try {
    const dateStr = date instanceof Date ? date.toISOString().split('T')[0] : date;
    const url = `${API_BASE_URL}/equipment-planner/eqp-planner?date=${dateStr}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching equipment planner data:', error);
    throw error;
  }
};

/**
 * Create equipment plan
 * 
 * @param {Object} payload - Plan data
 * @param {string} payload.eqp_plan_type - Plan type: "Operation" for manufacturing, "Cleaning" for cleaning/maintenance
 * @param {string} payload.eqp_activity - Activity type (e.g., "Type-A" for cleaning, "" for operation)
 * @param {string} payload.equipment_id - Equipment ID from equipment list (e.g., PRE007)
 * @param {string} payload.start_date - Start date in format "YYYY-MM-DD HH:mm:ss"
 * @param {string} payload.end_date - End date in format "YYYY-MM-DD HH:mm:ss"
 * @param {string} payload.despensing_request_id - Despensing request ID (batch number for operation, "" for cleaning/maintenance)
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   message?: string,
 *   data?: any
 * }>}
 */
export const createEquipmentPlan = async (payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/create-equipment-planner`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating equipment plan:', error);
    throw error;
  }
};

/**
 * Update equipment plan
 * 
 * @param {number} id - Activity ID to update
 * @param {Object} payload - Plan data (same structure as create)
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   message?: string,
 *   data?: any
 * }>}
 */
export const updateEquipmentPlan = async (id, payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/update-equipment-planner/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating equipment plan:', error);
    throw error;
  }
};

/**
 * Update variance reason and remarks for an equipment plan
 * Uses the create endpoint with ID t update existing record
 * 
 * @param {number} id - Activity ID to update
 * @param {string} reasonCatagory - Reason category for variance
 * @param {string} remarks - Additional remarks
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   message?: string,
 *   data?: any
 * }>}
 */
export const updateVarianceReason = async (id, reasonCatagory, remarks) => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/create-equipment-planner`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id,
        reasonCatagory,
        remarks
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating variance reason:', error);
    throw error;
  }
};

/**
 * Delete equipment plan
 * 
 * @param {number} id - Activity ID to delete
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   message?: string,
 *   data?: any
 * }>}
 */
export const deleteEquipmentPlan = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/delete-equipment-planner/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting equipment plan:', error);
    throw error;
  }
};

/**
 * Fetch planning data by area
 * 
 * @param {string|Date} date - Date in format YYYY-MM-DD or Date object (defaults to today)
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   activities?: Array<{
 *     id: number,
 *     area_id: string,
 *     area_name: string,
 *     activity_type: 'manufacturing' | 'cleaning' | 'maintenance',
 *     batch_number: string | null,
 *     cleaning_type: string | null,
 *     start_time: string,
 *     end_time: string
 *   }>,
 *   meta?: {
 *     display_start_time: string,
 *     display_end_time: string,
 *     total_activities: number
 *   }
 * }>}
 */
export const fetchPlanningByArea = async (date = null) => {
  try {
    const currentDate = date || new Date();
    const dateStr = currentDate instanceof Date ? currentDate.toISOString().split('T')[0] : currentDate;
    
    const url = new URL(`${API_BASE_URL}/equipment-planner/planning-by-area`);
    url.searchParams.append('date', dateStr);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching planning by area:', error);
    throw error;
  }
};

/**
 * Fetch plan history for a specific version
 * 
 * @param {Object} params - Query parameters
 * @param {number} params.version - Plan version number
 * @param {string} params.start_date - Start date (YYYY-MM-DD)
 * @param {string} params.end_date - End date (YYYY-MM-DD)
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   data: Array<{
 *     id: number,
 *     original_id: number,
 *     end_date: string,
 *     eqp_plan_type: string,
 *     product_batch: string | null,
 *     product_name: string,
 *     eqp_activity: string,
 *     eqp_id: string,
 *     start_date: string,
 *     is_active: boolean,
 *     plan_version: number,
 *     published_at: string
 *   }>
 * }>}
 */
export const fetchPlanHistory = async ({ version, start_date, end_date }) => {
  try {
    const url = new URL(`${API_BASE_URL}/equipment-planner/plan-history`);
    url.searchParams.append('version', version);
    url.searchParams.append('start_date', start_date);
    url.searchParams.append('end_date', end_date);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching plan history:', error);
    throw error;
  }
};

/**
 * Publish equipment plan
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   message?: string
 * }>}
 */
export const publishPlan = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/publish`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error publishing plan:', error);
    throw error;
  }
};
/*
* post upload planner AT report doc api 
*/
export const uploadPlannerDocument = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/equipment-planner/upload-planner-documents`, {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Upload failed');
    }

    return data;
  } catch (error) {
    console.error('Error uploading planner document:', error);
    throw error;
  }
};

/**
 * Fetch planner document (PDF info) by product batch number
 * 
 * @param {string} productBatchNo - The product batch number
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   data: {
 *     id: number,
 *     product_batch_no: string,
 *     pdf_url: string,
 *     dispensing_request_id: string | null,
 *     createdAt: string,
 *     updatedAt: string
 *   } | null
 * }>}
 */
export const fetchPlannerDocument = async (eqpCode, productBatchNo = null, docsbyeqpcode = false) => {
  try {
 

    const params = docsbyeqpcode === false
      ? `product_batch_no=${encodeURIComponent(productBatchNo)}&eqp_code=${encodeURIComponent(eqpCode)}`
      : `eqp_code=${encodeURIComponent(eqpCode)}&docsbyeqpcode=true`;  // ← matches controller

    const response = await fetch(
      `${API_BASE_URL}/equipment-planner/get-planner-document?${params}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } }
    );

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    return await response.json();
  } catch (error) {
    console.error('Error fetching planner document:', error);
    throw error;
  }
};

/**
 * Fetch file from blob storage
 * 
 * @param {string} blobName - The blob file name (e.g., "621af71a-1f98-4a4d-8d6b-9725f7a5cb8a.pdf")
 * 
 * @returns {Promise<Blob>}
 */
export const fetchFileFromBlob = async (blobName) => {
  try {
  
    const response = await fetch(
      `${API_BASE_URL}/user/fetch_file?blobName=${encodeURIComponent(blobName)}`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Get blob and ensure correct MIME type for PDF
    const blob = await response.blob();
    
    // If the blob doesn't have the correct PDF type, create a new blob with correct type
    if (blobName.toLowerCase().endsWith('.pdf') && blob.type !== 'application/pdf') {
      return new Blob([blob], { type: 'application/pdf' });
    }
    
    return blob;
  } catch (error) {
    console.error('Error fetching file from blob:', error);
    throw error;
  }
};

// ============================================
// PHYSICAL LOG ENTRY APIs (Stubs - TODO: Implement when API is ready)
// ============================================

/**
 * Fetch physical log activities for a specific date
 * 
 * @param {string} date - Date in DD-MM-YYYY format
 * @returns {Promise<{
 *   success: boolean,
 *   data: Array<{
 *     id: number,
 *     eqp_id: string,
 *     eqp_plan_type: string,
 *     eqp_activity: string,
 *     product_batch: string | null,
 *     product_name: string | null,
 *     start_date: string,
 *     end_date: string,
 *     is_active: boolean
 *   }>
 * }>}
 */
export const fetchPhysicalLogActivities = async (date) => {
  try {
    const url = new URL(`${API_BASE_URL}/equipment-planner/physical-log-entry`);
    url.searchParams.append('date', date);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching physical log activities:', error);
    throw error;
  }
};

/**
 * Save a physical log activity entry
 * 
 * @param {Object} activityData - The activity data to save
 * @param {string} activityData.eqp_plan_type - 'Operation' | 'Cleaning' | 'Maintenance'
 * @param {string} activityData.eqp_activity - Activity subtype (e.g., 'PRODUCTION', 'Type-A', 'PMP')
 * @param {string} activityData.eqp_id - Equipment ID
 * @param {string} activityData.start_date - Start datetime in 'YYYY-MM-DD HH:mm:ss' format
 * @param {string} activityData.end_date - End datetime in 'YYYY-MM-DD HH:mm:ss' format
 * @param {string} [activityData.product_batch] - Batch number (for manufacturing)
 * @param {string} [activityData.product_name] - Product name (for manufacturing)
 * @param {boolean} [activityData.is_active] - Whether the entry is active (default: true)
 * @returns {Promise<{
 *   success: boolean,
 *   data: Object,
 *   message: string
 * }>}
 */
export const savePhysicalLogActivity = async (activityData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/create-physical-log-entry`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...activityData,
        is_active: true
      }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || data.message || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    console.error('Error saving physical log activity:', error);
    throw error;
  }
};

/**
 * Update an existing physical log entry
 * 
 * @param {number} id - The ID of the entry to update
 * @param {Object} activityData - Activity data to update
 * @returns {Promise<{success: boolean, data: Object, message: string}>}
 */
export const updatePhysicalLogActivity = async (id, activityData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/update-physical-log-entry?id=${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(activityData),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || data.message || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    console.error('Error updating physical log activity:', error);
    throw error;
  }
};

/**
 * Delete a physical log entry
 * 
 * @param {number} id - The ID of the entry to delete
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const deletePhysicalLogActivity = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/delete-physical-log-entry`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error deleting physical log activity:', error);
    throw error;
  }
};

// ============================================
// EQUIPMENT LOG ACCURACY DASHBOARD APIs
// ============================================

/**
 * Fetch equipment types for the accuracy dashboard
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   data: string[]
 * }>}
 */
export const fetchEquipmentTypes = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/equipment-types`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching equipment types:', error);
    throw error;
  }
};

/**
 * Fetch reconciliation summary for equipment log accuracy
 * 
 * @param {Object} params - Query parameters
 * @param {string} params.from_date - Start date (YYYY-MM-DD)
 * @param {string} params.to_date - End date (YYYY-MM-DD)
 * @param {number} params.buffer - Buffer time in minutes
 * @param {string} [params.equipment_type] - Equipment type filter (default: 'ALL')
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   data: Array<{
 *     equipment_type: string,
 *     total_entries: number,
 *     match_found: number,
 *     wrong_timings: number,
 *     missing: number,
 *     digitally_logged: number
 *   }>,
 *   meta: {
 *     from_date: string,
 *     to_date: string,
 *     buffer_minutes: number,
 *     equipment_type: string,
 *     query_executed_at: string
 *   }
 * }>}
 */
export const fetchReconciliationSummary = async ({ from_date, to_date, buffer, equipment_type = 'ALL' }) => {
  try {
    const url = new URL(`${API_BASE_URL}/equipment-planner/reconciliation-summary`);
    url.searchParams.append('from_date', from_date);
    url.searchParams.append('to_date', to_date);
    url.searchParams.append('buffer', buffer);
    
    if (equipment_type) {
      url.searchParams.append('equipment_type', equipment_type);
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching reconciliation summary:', error);
    throw error;
  }
};

/**
 * Mark a planner document as inactive (soft delete)
 * 
 * @param {number} id - The document ID to mark as inactive
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   message: string,
 *   data: {
 *     id: number,
 *     product_batch_no: string,
 *     eqp_code: string,
 *     pdf_url: string,
 *     dispensing_request_id: string | null,
 *     is_active: boolean,
 *     createdAt: string,
 *     updatedAt: string
 *   }
 * }>}
 */
export const inactivatePlannerDocument = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/planner-document/${id}/inactive`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error inactivating planner document:', error);
    throw error;
  }
};

// ============================================
// HOLD TIME RULES APIs
// ============================================

export const fetchHoldTimeStages = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/hold-time-stages`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching hold time stages:', error);
    throw error;
  }
};

export const fetchHoldTimeRules = async ({ product_code = null, rule_type = null } = {}) => {
  try {
    const url = new URL(`${API_BASE_URL}/equipment-planner/hold-time-rules`);
    if (product_code) url.searchParams.append('product_code', product_code);
    if (rule_type) url.searchParams.append('rule_type', rule_type);
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching hold time rules:', error);
    throw error;
  }
};

export const createHoldTimeRule = async (payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/hold-time-rules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating hold time rule:', error);
    throw error;
  }
};

export const updateHoldTimeRule = async (id, payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/hold-time-rules/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating hold time rule:', error);
    throw error;
  }
};

export const deleteHoldTimeRule = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/hold-time-rules/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting hold time rule:', error);
    throw error;
  }
};

export const fetchHoldTimeTracker = async ({ batch_number = null, from_date = null, to_date = null, source = null } = {}) => {
  try {
    const url = new URL(`${API_BASE_URL}/equipment-planner/hold-time-tracker`);
    if (batch_number) url.searchParams.append('batch_number', batch_number);
    if (from_date) url.searchParams.append('from_date', from_date);
    if (to_date) url.searchParams.append('to_date', to_date);
    // 'area_log' = Batch Auto Tracker (stage times from the Area Log)
    if (source) url.searchParams.append('source', source);
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching hold time tracker:', error);
    throw error;
  }
};

export const postHoldTimeRatification = async (payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/hold-time-ratification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error posting hold time ratification:', error);
    throw error;
  }
};

// ═══════════════════════════════════════════════════════════
// WIP TRACKER API
// ═══════════════════════════════════════════════════════════

export const fetchWipTracker = async ({ from_date = null, to_date = null } = {}) => {
  try {
    const url = new URL(`${API_BASE_URL}/equipment-planner/wip-tracker`);
    if (from_date) url.searchParams.append('from_date', from_date);
    if (to_date) url.searchParams.append('to_date', to_date);
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching WIP tracker:', error);
    throw error;
  }
};

// ═══════════════════════════════════════════════════════════
// OSD DISPATCH APIs
// ═══════════════════════════════════════════════════════════

export const fetchDispatchStages = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/dispatch/masters/stages`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch dispatch stages');
    return data.data;
  } catch (error) {
    console.error('Error fetching dispatch stages:', error);
    throw error;
  }
};

export const fetchDispatchSteps = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/dispatch/masters/steps`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch dispatch steps');
    return data.data;
  } catch (error) {
    console.error('Error fetching dispatch steps:', error);
    throw error;
  }
};

export const fetchProductDrugCodes = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/dispatch/masters/products`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch product drug codes');
    return data.data;
  } catch (error) {
    console.error('Error fetching product drug codes:', error);
    throw error;
  }
};

export const fetchProductMfgStages = async (productDrugCodeId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/dispatch/masters/product-stages/${productDrugCodeId}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch product manufacturing stages');
    return data.data;
  } catch (error) {
    console.error('Error fetching product manufacturing stages:', error);
    throw error;
  }
};

export const fetchDispatchBatches = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.status)    query.append('status', params.status);
    if (params.product)   query.append('product', params.product);
    if (params.date_from) query.append('date_from', params.date_from);
    if (params.date_to)   query.append('date_to', params.date_to);
    if (params.filter_field) query.append('filter_field', params.filter_field);
    const response = await fetch(`${API_BASE_URL}/dispatch/batches?${query.toString()}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch dispatch batches');
    return data.data;
  } catch (error) {
    console.error('Error fetching dispatch batches:', error);
    throw error;
  }
};

export const syncDispatchBatches = async (params = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}/dispatch/batches/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params)
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to sync dispatch batches');
    return data;
  } catch (error) {
    console.error('Error syncing dispatch batches:', error);
    throw error;
  }
};

export const createDispatchBatch = async (blendBatchNo, createdBy) => {
  try {
    const response = await fetch(`${API_BASE_URL}/dispatch/batches/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ blend_batch_no: blendBatchNo, created_by: createdBy })
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to create dispatch batch');
    return data.data;
  } catch (error) {
    console.error('Error creating dispatch batch:', error);
    throw error;
  }
};

export const fetchBatchChecklist = async (batchTrackerId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/dispatch/batch/${batchTrackerId}/checklist`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch batch checklist');
    return data.data;
  } catch (error) {
    console.error('Error fetching batch checklist:', error);
    throw error;
  }
};

export const tickDispatchStep = async (batchTrackerId, stepId, tickedBy) => {
  try {
    const response = await fetch(`${API_BASE_URL}/dispatch/batch/${batchTrackerId}/tick`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ step_id: stepId, ticked_by: tickedBy })
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to tick step');
    return data.data;
  } catch (error) {
    console.error('Error ticking dispatch step:', error);
    throw error;
  }
};

export const untickDispatchStep = async (batchTrackerId, stepId, untickedBy, untickReason) => {
  try {
    const response = await fetch(`${API_BASE_URL}/dispatch/batch/${batchTrackerId}/untick`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ step_id: stepId, unticked_by: untickedBy, untick_reason: untickReason })
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to untick step');
    return data;
  } catch (error) {
    console.error('Error unticking dispatch step:', error);
    throw error;
  }
};

export const triggerAutoTick = async (batchTrackerId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/dispatch/batch/${batchTrackerId}/auto-tick`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to auto-tick');
    return data;
  } catch (error) {
    console.error('Error triggering auto-tick:', error);
    throw error;
  }
};

export const toggleConditionalStage = async (batchTrackerId, conditionKey, enable, stageName, removedBy, removeReason) => {
  try {
    const body = { condition_key: conditionKey, enable, stage_name: stageName };
    if (!enable && removedBy) body.removed_by = removedBy;
    if (!enable && removeReason) body.remove_reason = removeReason;
    const response = await fetch(`${API_BASE_URL}/dispatch/batch/${batchTrackerId}/conditional`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to toggle conditional stage');
    return data.data;
  } catch (error) {
    console.error('Error toggling conditional stage:', error);
    throw error;
  }
};

export const fetchPendingGrid = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.batch_id)    query.append('batch_id',    params.batch_id);
    if (params.team)        query.append('team',        params.team);
    if (params.product)     query.append('product',     params.product);
    if (params.date_from)   query.append('date_from',   params.date_from);
    if (params.date_to)     query.append('date_to',     params.date_to);
    if (params.in_progress !== undefined) query.append('in_progress', params.in_progress);
    if (params.completed   !== undefined) query.append('completed',   params.completed);
    if (params.filter_field) query.append('filter_field', params.filter_field);
    const response = await fetch(`${API_BASE_URL}/dispatch/pending-grid?${query.toString()}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch pending grid');
    return data.data;
  } catch (error) {
    console.error('Error fetching pending grid:', error);
    throw error;
  }
};

export const fetchTeamKanban = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.date_from)   query.append('date_from',   params.date_from);
    if (params.date_to)     query.append('date_to',     params.date_to);
    if (params.in_progress !== undefined) query.append('in_progress', params.in_progress);
    if (params.completed   !== undefined) query.append('completed',   params.completed);
    if (params.product)     query.append('product',     params.product);
    if (params.filter_field) query.append('filter_field', params.filter_field);
    const response = await fetch(`${API_BASE_URL}/dispatch/team-kanban?${query.toString()}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch team kanban');
    return data.data;
  } catch (error) {
    console.error('Error fetching team kanban:', error);
    throw error;
  }
};

export const fetchDispatchTimeline = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.date_from)   query.append('date_from',   params.date_from);
    if (params.date_to)     query.append('date_to',     params.date_to);
    if (params.in_progress !== undefined) query.append('in_progress', params.in_progress);
    if (params.completed   !== undefined) query.append('completed',   params.completed);
    if (params.product)     query.append('product',     params.product);
    if (params.filter_field) query.append('filter_field', params.filter_field);
    const response = await fetch(`${API_BASE_URL}/dispatch/timeline?${query.toString()}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch dispatch timeline');
    return data.data;
  } catch (error) {
    console.error('Error fetching dispatch timeline:', error);
    throw error;
  }
};

export const fetchBatchPhysicalLogStatus = async (batchTrackerId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/dispatch/batch/${batchTrackerId}/physical-log-status`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch physical log status');
    return data.data;
  } catch (error) {
    console.error('Error fetching batch physical log status:', error);
    throw error;
  }
};

export const fetchDispatchPlanner = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.months)    query.append('months', params.months);
    if (params.from)      query.append('from',   params.from);
    if (params.date_from) query.append('date_from', params.date_from);
    if (params.date_to)   query.append('date_to',   params.date_to);
    const response = await fetch(`${API_BASE_URL}/dispatch/planner?${query.toString()}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch dispatch planner');
    return data.data;
  } catch (error) {
    console.error('Error fetching dispatch planner:', error);
    throw error;
  }
};

export const assignDispatchWeeks = async (updates) => {
  try {
    const response = await fetch(`${API_BASE_URL}/dispatch/planner/assign`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to assign dispatch weeks');
    return data;
  } catch (error) {
    console.error('Error assigning dispatch weeks:', error);
    throw error;
  }
};

// ─────────────────────────────────────────────────────────────
// PACKING DISPATCH TRACKER
// ─────────────────────────────────────────────────────────────
export const fetchPackingBatches = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.from_date)     query.append('from_date',     params.from_date);
    if (params.to_date)       query.append('to_date',       params.to_date);
    if (params.packing_batch) query.append('packing_batch', params.packing_batch);
    if (params.parent_batch)  query.append('parent_batch',  params.parent_batch);
    if (params.product)       query.append('product',       params.product);
    if (params.status)        query.append('status',        params.status);
    if (params.filter_field)  query.append('filter_field',  params.filter_field);
    const response = await fetch(`${API_BASE_URL}/packing-tracker/list?${query.toString()}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch packing batches');
    return data.data;
  } catch (error) {
    console.error('Error fetching packing batches:', error);
    throw error;
  }
};

export const fetchPackingChecklist = async (trackerId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/packing-tracker/checklist/${trackerId}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch packing checklist');
    return data.data;
  } catch (error) {
    console.error('Error fetching packing checklist:', error);
    throw error;
  }
};

export const tickPackingStep = async (trackerId, stepId, tickedBy) => {
  try {
    const response = await fetch(`${API_BASE_URL}/packing-tracker/checklist/${trackerId}/tick`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ step_id: stepId, action: 'tick', ticked_by: tickedBy }),
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to tick step');
    return data;
  } catch (error) {
    console.error('Error ticking packing step:', error);
    throw error;
  }
};

export const untickPackingStep = async (trackerId, stepId, untickedBy, untickReason) => {
  try {
    const response = await fetch(`${API_BASE_URL}/packing-tracker/checklist/${trackerId}/tick`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ step_id: stepId, action: 'untick', ticked_by: untickedBy, untick_reason: untickReason }),
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to untick step');
    return data;
  } catch (error) {
    console.error('Error unticking packing step:', error);
    throw error;
  }
};

export const togglePackingFlag = async (trackerId, flag, enable, stage, removedBy, removeReason) => {
  try {
    const response = await fetch(`${API_BASE_URL}/packing-tracker/tracker/${trackerId}/flags`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ flag, enable, stage, removed_by: removedBy, remove_reason: removeReason }),
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to toggle flag');
    return data;
  } catch (error) {
    console.error('Error toggling packing flag:', error);
    throw error;
  }
};

export const syncPackingBatches = async (params = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}/packing-tracker/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to sync packing batches');
    return data;
  } catch (error) {
    console.error('Error syncing packing batches:', error);
    throw error;
  }
};

export const triggerPackingAutoTick = async (trackerId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/packing-tracker/batch/${trackerId}/auto-tick`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to auto-tick');
    return data;
  } catch (error) {
    console.error('Error triggering packing auto-tick:', error);
    throw error;
  }
};

export const fetchPackingPendingGrid = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.product)     query.append('product', params.product);
    if (params.date_from)   query.append('date_from', params.date_from);
    if (params.date_to)     query.append('date_to', params.date_to);
    if (params.in_progress !== undefined) query.append('in_progress', params.in_progress);
    if (params.completed   !== undefined) query.append('completed', params.completed);
    if (params.filter_field) query.append('filter_field', params.filter_field);
    const response = await fetch(`${API_BASE_URL}/packing-tracker/pending-grid?${query.toString()}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch packing pending grid');
    return data.data;
  } catch (error) {
    console.error('Error fetching packing pending grid:', error);
    throw error;
  }
};

export const fetchPackingTeamKanban = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.date_from)   query.append('date_from', params.date_from);
    if (params.date_to)     query.append('date_to', params.date_to);
    if (params.in_progress !== undefined) query.append('in_progress', params.in_progress);
    if (params.completed   !== undefined) query.append('completed', params.completed);
    if (params.product)     query.append('product', params.product);
    if (params.filter_field) query.append('filter_field', params.filter_field);
    const response = await fetch(`${API_BASE_URL}/packing-tracker/team-kanban?${query.toString()}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch packing team kanban');
    return data.data;
  } catch (error) {
    console.error('Error fetching packing team kanban:', error);
    throw error;
  }
};

export const fetchPackingTimeline = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.date_from)   query.append('date_from', params.date_from);
    if (params.date_to)     query.append('date_to', params.date_to);
    if (params.in_progress !== undefined) query.append('in_progress', params.in_progress);
    if (params.completed   !== undefined) query.append('completed', params.completed);
    if (params.product)     query.append('product', params.product);
    if (params.filter_field) query.append('filter_field', params.filter_field);
    const response = await fetch(`${API_BASE_URL}/packing-tracker/timeline?${query.toString()}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch packing timeline');
    return data.data;
  } catch (error) {
    console.error('Error fetching packing timeline:', error);
    throw error;
  }
};

export const fetchPackingPlanner = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.months)    query.append('months', params.months);
    if (params.from)      query.append('from', params.from);
    if (params.date_from) query.append('date_from', params.date_from);
    if (params.date_to)   query.append('date_to',   params.date_to);
    const response = await fetch(`${API_BASE_URL}/packing-tracker/planner?${query.toString()}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch packing planner');
    return data.data;
  } catch (error) {
    console.error('Error fetching packing planner:', error);
    throw error;
  }
};

export const assignPackingWeeks = async (updates) => {
  try {
    const response = await fetch(`${API_BASE_URL}/packing-tracker/assign-weeks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to assign packing weeks');
    return data;
  } catch (error) {
    console.error('Error assigning packing weeks:', error);
    throw error;
  }
};

// ═══════════════════════════════════════════════════════════
// MANUFACTURING DISPATCH TRACKER
// ═══════════════════════════════════════════════════════════

export const fetchManufacturingBatches = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.from_date)     query.append('from_date', params.from_date);
    if (params.to_date)       query.append('to_date', params.to_date);
    if (params.manufacturing_batch) query.append('manufacturing_batch', params.manufacturing_batch);
    if (params.parent_batch)  query.append('parent_batch', params.parent_batch);
    if (params.product)       query.append('product', params.product);
    if (params.status)        query.append('status', params.status);
    if (params.filter_field)  query.append('filter_field', params.filter_field);
    const response = await fetch(`${API_BASE_URL}/manufacturing-tracker/list?${query.toString()}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch manufacturing batches');
    return data.data;
  } catch (error) {
    console.error('Error fetching manufacturing batches:', error);
    throw error;
  }
};

export const fetchManufacturingChecklist = async (trackerId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/manufacturing-tracker/checklist/${trackerId}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch manufacturing checklist');
    return data.data;
  } catch (error) {
    console.error('Error fetching manufacturing checklist:', error);
    throw error;
  }
};

export const tickManufacturingStep = async (trackerId, stepId, tickedBy) => {
  try {
    const response = await fetch(`${API_BASE_URL}/manufacturing-tracker/checklist/${trackerId}/tick`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ step_id: stepId, action: 'tick', ticked_by: tickedBy }),
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to tick step');
    return data;
  } catch (error) {
    console.error('Error ticking manufacturing step:', error);
    throw error;
  }
};

export const untickManufacturingStep = async (trackerId, stepId, untickedBy, untickReason) => {
  try {
    const response = await fetch(`${API_BASE_URL}/manufacturing-tracker/checklist/${trackerId}/tick`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ step_id: stepId, action: 'untick', ticked_by: untickedBy, untick_reason: untickReason }),
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to untick step');
    return data;
  } catch (error) {
    console.error('Error unticking manufacturing step:', error);
    throw error;
  }
};

export const toggleManufacturingFlag = async (trackerId, flag, enable, stage, removedBy, removeReason) => {
  try {
    const response = await fetch(`${API_BASE_URL}/manufacturing-tracker/tracker/${trackerId}/flags`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ flag, enable, stage, removed_by: removedBy, remove_reason: removeReason }),
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to toggle flag');
    return data;
  } catch (error) {
    console.error('Error toggling manufacturing flag:', error);
    throw error;
  }
};

export const triggerManufacturingAutoTick = async (trackerId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/manufacturing-tracker/batch/${trackerId}/auto-tick`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to auto-tick');
    return data;
  } catch (error) {
    console.error('Error triggering manufacturing auto-tick:', error);
    throw error;
  }
};

export const fetchManufacturingPendingGrid = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.product)     query.append('product', params.product);
    if (params.date_from)   query.append('date_from', params.date_from);
    if (params.date_to)     query.append('date_to', params.date_to);
    if (params.in_progress !== undefined) query.append('in_progress', params.in_progress);
    if (params.completed   !== undefined) query.append('completed', params.completed);
    if (params.filter_field) query.append('filter_field', params.filter_field);
    const response = await fetch(`${API_BASE_URL}/manufacturing-tracker/pending-grid?${query.toString()}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch manufacturing pending grid');
    return data.data;
  } catch (error) {
    console.error('Error fetching manufacturing pending grid:', error);
    throw error;
  }
};

export const fetchManufacturingTeamKanban = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.date_from)   query.append('date_from', params.date_from);
    if (params.date_to)     query.append('date_to', params.date_to);
    if (params.in_progress !== undefined) query.append('in_progress', params.in_progress);
    if (params.completed   !== undefined) query.append('completed', params.completed);
    if (params.product)     query.append('product', params.product);
    if (params.filter_field) query.append('filter_field', params.filter_field);
    const response = await fetch(`${API_BASE_URL}/manufacturing-tracker/team-kanban?${query.toString()}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch manufacturing team kanban');
    return data.data;
  } catch (error) {
    console.error('Error fetching manufacturing team kanban:', error);
    throw error;
  }
};

export const fetchManufacturingTimeline = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.date_from)   query.append('date_from', params.date_from);
    if (params.date_to)     query.append('date_to', params.date_to);
    if (params.in_progress !== undefined) query.append('in_progress', params.in_progress);
    if (params.completed   !== undefined) query.append('completed', params.completed);
    if (params.product)     query.append('product', params.product);
    if (params.filter_field) query.append('filter_field', params.filter_field);
    const response = await fetch(`${API_BASE_URL}/manufacturing-tracker/timeline?${query.toString()}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch manufacturing timeline');
    return data.data;
  } catch (error) {
    console.error('Error fetching manufacturing timeline:', error);
    throw error;
  }
};

export const fetchManufacturingPlanner = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.months)    query.append('months', params.months);
    if (params.from)      query.append('from', params.from);
    if (params.date_from) query.append('date_from', params.date_from);
    if (params.date_to)   query.append('date_to', params.date_to);
    const response = await fetch(`${API_BASE_URL}/manufacturing-tracker/planner?${query.toString()}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch manufacturing planner');
    return data.data;
  } catch (error) {
    console.error('Error fetching manufacturing planner:', error);
    throw error;
  }
};

export const assignManufacturingWeeks = async (updates) => {
  try {
    const response = await fetch(`${API_BASE_URL}/manufacturing-tracker/assign-weeks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to assign manufacturing weeks');
    return data;
  } catch (error) {
    console.error('Error assigning manufacturing weeks:', error);
    throw error;
  }
};

// Legacy exports for backward compatibility
export const fetchActivities = fetchPlanning;
export const fetchEquipment = fetchEquipmentList;

/* ============================================================
 * R&D EQUIPMENT PLANNER + PHYSICAL LOG
 * Base: /rnd-equipment-planner
 * Parallels the /equipment-planner helpers above, but points at
 * the R&D tables and omits volume options / product dropdowns.
 * ============================================================ */

export const fetchRndEquipmentList = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd-equipment-planner/equipment-list`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    if (!data.success) throw new Error('API returned unsuccessful response');
    return data;
  } catch (error) {
    console.error('Error fetching R&D equipment list:', error);
    throw error;
  }
};

export const fetchRndEqpPlanner = async (date) => {
  try {
    const dateStr = date instanceof Date ? date.toISOString().split('T')[0] : date;
    const url = `${API_BASE_URL}/rnd-equipment-planner/eqp-planner?date=${dateStr}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching R&D equipment planner data:', error);
    throw error;
  }
};

export const createRndEquipmentPlan = async (payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd-equipment-planner/create-rnd-equipment-planner`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error creating R&D equipment plan:', error);
    throw error;
  }
};

export const updateRndVarianceReason = async (id, reasonCatagory, remarks) => {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd-equipment-planner/create-rnd-equipment-planner`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, reasonCatagory, remarks }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error updating R&D variance reason:', error);
    throw error;
  }
};

export const deleteRndEquipmentPlan = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd-equipment-planner/delete-rnd-equipment-planner/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error deleting R&D equipment plan:', error);
    throw error;
  }
};

export const fetchRndPlanVersions = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd-equipment-planner/plan-versions`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    if (!data.success) throw new Error('API returned unsuccessful response');
    return data;
  } catch (error) {
    console.error('Error fetching R&D plan versions:', error);
    throw error;
  }
};

export const fetchRndPlanHistory = async ({ version, start_date, end_date }) => {
  try {
    const url = new URL(`${API_BASE_URL}/rnd-equipment-planner/plan-history`);
    url.searchParams.append('version', version);
    url.searchParams.append('start_date', start_date);
    url.searchParams.append('end_date', end_date);
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    if (!data.success) throw new Error('API returned unsuccessful response');
    return data;
  } catch (error) {
    console.error('Error fetching R&D plan history:', error);
    throw error;
  }
};

export const publishRndPlan = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd-equipment-planner/publish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error publishing R&D plan:', error);
    throw error;
  }
};

export const fetchRndPhysicalLogActivities = async (date) => {
  try {
    const url = new URL(`${API_BASE_URL}/rnd-equipment-planner/physical-log-entry`);
    url.searchParams.append('date', date);
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'API returned unsuccessful response');
    return data;
  } catch (error) {
    console.error('Error fetching R&D physical log activities:', error);
    throw error;
  }
};

export const saveRndPhysicalLogActivity = async (activityData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd-equipment-planner/create-physical-log-entry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...activityData, is_active: true }),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || data.message || `HTTP error! status: ${response.status}`);
    }
    return data;
  } catch (error) {
    console.error('Error saving R&D physical log activity:', error);
    throw error;
  }
};

export const updateRndPhysicalLogActivity = async (id, activityData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd-equipment-planner/update-physical-log-entry?id=${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(activityData),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || data.message || `HTTP error! status: ${response.status}`);
    }
    return data;
  } catch (error) {
    console.error('Error updating R&D physical log activity:', error);
    throw error;
  }
};

export const deleteRndPhysicalLogActivity = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd-equipment-planner/delete-physical-log-entry`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'API returned unsuccessful response');
    return data;
  } catch (error) {
    console.error('Error deleting R&D physical log activity:', error);
    throw error;
  }
};

/* ============================================================
 * AREA-WISE PLANNER
 * Base: /area-planner
 *
 * Backed by its own tables (area_planner, area_planner_versions,
 * area_plan_history) - a replica of the equipment planner trio keyed
 * on area_id from area_masters. Area plans version and publish
 * independently of equipment plans.
 *
 * The controller accepts the eqp_id / eqp_plan_type / eqp_activity
 * keys this screen sends as aliases of area_id / area_plan_type /
 * area_activity, and returns both the equipment_* keys the grid reads
 * and the area_* keys that name the data correctly.
 * ============================================================ */

const AREA_PLANNER_BASE = `${API_BASE_URL}/area-planner`;

/**
 * Fetch the planning areas from area_masters.
 *
 * Same rows as /equipment-planner/get-area-list plus area_label and
 * area_sequence, which the grid uses to order the rows.
 *
 * @returns {Promise<{
 *   success: boolean,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     area_name: string,
 *     area_id: string,
 *     area_label: string | null,
 *     area_sequence: number | null
 *   }>,
 *   count: number
 * }>}
 */
export const fetchAreaPlannerAreaList = async () => {
  try {
    const response = await fetch(`${AREA_PLANNER_BASE}/area-list`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'API returned unsuccessful response');
    }
    return data;
  } catch (error) {
    console.error('Error fetching area planner area list:', error);
    throw error;
  }
};

/**
 * Fetch the area planner grid for a single date.
 *
 * @param {string|Date} date - Date in format YYYY-MM-DD or Date object
 * @param {number} [versionId] - Optional. Read the plan as it stood at this version.
 *
 * @returns {Promise<{
 *   success: boolean,
 *   activities?: Array<{
 *     id: number,
 *     equipment_id: string,
 *     equipment_name: string,
 *     area_id: string,
 *     area_name: string,
 *     activity_type: string,
 *     batch_number: string | null,
 *     product_name: string | null,
 *     cleaning_type: string | null,
 *     start_time: string,
 *     end_time: string,
 *     option_selected: string | null,
 *     reasonCatagory: string | null,
 *     remarks: string | null
 *   }>
 * }>}
 */
export const fetchAreaPlanner = async (date, versionId = null) => {
  try {
    const dateStr = date instanceof Date ? date.toISOString().split('T')[0] : date;
    const params = new URLSearchParams({ date: dateStr });
    if (versionId) {
      params.append('version_id', versionId);
    }

    // No Content-Type header. A GET has no body, so it does nothing -- and
    // `application/json` is not a CORS-safelisted value, so sending it turns
    // every one of these into a preflight OPTIONS plus the GET. The grid used
    // to fetch fourteen days this way: twenty-eight round trips per refresh.
    const response = await fetch(`${AREA_PLANNER_BASE}/area-planner?${params.toString()}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching area planner data:', error);
    throw error;
  }
};

/**
 * Create or update an area plan entry. Always saved as draft.
 *
 * Accepts a single object, an array for bulk create, or { id, is_active: false }
 * to soft delete - mirroring createEquipmentPlan.
 *
 * @param {Object|Array<Object>} payload
 * @returns {Promise<{ success: boolean, message?: string, data?: any }>}
 */
export const createAreaPlan = async (payload) => {
  try {
    const response = await fetch(`${AREA_PLANNER_BASE}/create-area-planner`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating area plan:', error);
    throw error;
  }
};

/**
 * The whole grid window in one request, keyed by date.
 *
 * Replaces fourteen per-day calls. Each of those also carried a
 * `Content-Type` header, which is not CORS-safelisted for `application/json`
 * and so forced a preflight -- twenty-eight round trips per grid refresh, and
 * a create triggers two or three refreshes.
 *
 * Measured against this backend: the fourteen calls in parallel take 437ms,
 * this takes 165ms, and it was verified to return byte-identical rows for
 * every one of the fourteen days before anything was wired to it.
 *
 * Returns `{ days: { 'YYYY-MM-DD': [rows] } }` -- a row appears under every
 * day it overlaps, exactly as asking for those days separately produced, so
 * the grid's own parsing does not change.
 */
export const fetchAreaPlannerRange = async (startDate, endDate) => {
  const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
  const response = await fetch(`${AREA_PLANNER_BASE}/area-planner-range?${params.toString()}`, {
    method: 'GET',
  });
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`);
    // 404/403 means this backend predates the route; the caller falls back to
    // the per-day fetches. Anything else is a real failure.
    if (response.status === 404 || response.status === 403) error.endpointMissing = true;
    throw error;
  }
  return response.json();
};

/**
 * Push an activity later and slide the rest of that area's plan with it.
 *
 * The whole cascade happens in one request inside one database transaction:
 * the backend saves the edit, works out the delta from `oldEnd`, finds every
 * later row in that area however far out the plan runs, and moves them all by
 * that delta. Measured at 281ms for 140 rows, against 17.7s for the old
 * one-request-per-row approach -- and because it is a single transaction, a
 * failure or a cancel leaves the plan exactly as it was.
 *
 * Pass `preview: true` to get back what WOULD move, writing nothing. That is
 * what fills the confirmation dialog.
 *
 * THE RETURN SHAPE MATTERS, because the caller must know whether it is safe to
 * fall back to the old client-side cascade:
 *
 *   resolves { ok: true, ... }        -> it worked
 *   resolves { ok: false, refused }   -> the backend refused (completed work in
 *                                        the way). Nothing was written. Show
 *                                        the blockers. DO NOT fall back.
 *   throws   err.endpointMissing      -> 404/403, this backend does not have
 *                                        the route yet. Falling back is safe.
 *   throws   anything else            -> the request may have COMMITTED before
 *                                        the response was lost. Falling back
 *                                        would shift the plan twice, and there
 *                                        is no undo. Never fall back here.
 */
export const shiftPlanForward = async ({ areaId, oldEnd, activity, preview = false }) => {
  const response = await fetch(`${AREA_PLANNER_BASE}/shift-forward`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ area_id: areaId, old_end: oldEnd, preview, activity }),
  });

  const data = await response.json().catch(() => ({}));

  if (response.ok) return { ok: true, ...data };

  if (response.status === 404 || response.status === 403) {
    const error = new Error('shift-forward is not available on this backend');
    error.endpointMissing = true;
    throw error;
  }

  // 409 is a decision, not a failure: the backend looked, refused, and rolled
  // back. Returning it rather than throwing keeps it off the fallback path.
  if (response.status === 409) return { ok: false, refused: true, ...data };

  const error = new Error(data.message || `HTTP error! status: ${response.status}`);
  error.status = response.status;
  throw error;
};

/**
 * Soft delete MANY area plan rows in a single request.
 *
 * The create endpoint's `{ id, is_active: false }` branch passes `id` straight
 * into Sequelize's `where: { id }`, and Sequelize renders an array as
 * `WHERE id IN (...)`. So one call deactivates the whole list -- verified
 * against the running API: 12 ids, one request, all 12 deactivated.
 *
 * This replaces N sequential deletes (measured 324s for 100 rows) with one.
 *
 * Caveat worth knowing: the endpoint was written for a single id, so it reports
 * one result for the whole batch rather than per row. Callers that need to know
 * exactly which rows failed should fall back to one-at-a-time on error.
 *
 * @param {Array<number>} ids
 */
export const deleteManyAreaPlans = async (ids) => {
  if (!Array.isArray(ids) || ids.length === 0) return { success: true, data: [] };
  const response = await fetch(`${AREA_PLANNER_BASE}/create-area-planner`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: ids, is_active: false }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

/**
 * Soft delete an area plan entry.
 *
 * @param {number} id - area_planner row id
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export const deleteAreaPlan = async (id) => {
  try {
    const response = await fetch(`${AREA_PLANNER_BASE}/delete-area-planner/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting area plan:', error);
    throw error;
  }
};

/**
 * Publish the area plan - flips drafts to published, bumps the version and
 * snapshots every active row into area_plan_history.
 *
 * @returns {Promise<{
 *   success: boolean,
 *   message?: string,
 *   new_version?: number,
 *   version_id?: number,
 *   version_name?: string,
 *   records_published?: number,
 *   published_at?: string
 * }>}
 */
export const publishAreaPlan = async () => {
  try {
    const response = await fetch(`${AREA_PLANNER_BASE}/publish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error publishing area plan:', error);
    throw error;
  }
};

/**
 * Fetch the published area plan versions, newest first.
 *
 * @returns {Promise<{
 *   success: boolean,
 *   data: Array<{ plan_version: number, published_at: string, record_count: number }>,
 *   count: number
 * }>}
 */
export const fetchAreaPlanVersions = async () => {
  try {
    const response = await fetch(`${AREA_PLANNER_BASE}/plan-versions`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching area plan versions:', error);
    throw error;
  }
};

/**
 * Fetch the rows of one published area plan version.
 *
 * @param {Object} params
 * @param {number} params.version - Plan version number
 * @param {string} [params.start_date] - YYYY-MM-DD
 * @param {string} [params.end_date] - YYYY-MM-DD
 *
 * @returns {Promise<{ success: boolean, data: Array<Object>, meta: Object }>}
 */
export const fetchAreaPlanHistory = async ({ version, start_date, end_date }) => {
  try {
    const params = new URLSearchParams({ version });
    if (start_date) params.append('start_date', start_date);
    if (end_date) params.append('end_date', end_date);

    const response = await fetch(`${AREA_PLANNER_BASE}/plan-history?${params.toString()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching area plan history:', error);
    throw error;
  }
};

/**
 * Fetch rows from area_planner_versions. Their ids are what fetchAreaPlanner
 * takes as versionId for a snapshot read.
 *
 * @returns {Promise<{
 *   success: boolean,
 *   data: Array<{ id: number, version_name: string, description: string, createdAt: string }>,
 *   count: number
 * }>}
 */
export const fetchAreaPlannerVersions = async () => {
  try {
    const response = await fetch(`${AREA_PLANNER_BASE}/versions`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching area planner versions:', error);
    throw error;
  }
};

/**
 * Fetch draft/published counts and the current version for the area plan.
 *
 * @returns {Promise<{ success: boolean, data: Object }>}
 */
export const fetchAreaPlanStatus = async () => {
  try {
    const response = await fetch(`${AREA_PLANNER_BASE}/plan-status`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching area plan status:', error);
    throw error;
  }
};

/**
 * Fetch the area consolidated tracker: planned area activities vs what actually
 * happened, over a date range.
 *
 * Plan comes from area_planner / area_plan_history, actuals from
 * equipment_log_books (equipment_id holds the area_id). A plan and an actual are
 * the same activity when the area_id and the batch number both match.
 *
 * @param {Object} params
 * @param {string} params.start_date - "YYYY-MM-DD HH:mm:ss"
 * @param {string} params.end_date - "YYYY-MM-DD HH:mm:ss"
 * @param {number} [params.plan_version] - Compare against this published version
 * @param {string} [params.area_id] - Drill down to one area (used by Micro Level Monitoring).
 *                                    Omit for every area, which is what the tracker does.
 *
 * @returns {Promise<{
 *   success: boolean,
 *   data: Object.<string, {
 *     area_ids: string[],
 *     area_category: string,
 *     matched: Array<Object>,
 *     not_started: Array<Object>,
 *     unplanned: Array<Object>,
 *     activity_timeline: Array<Object>,
 *     unutilized_time: string | null,
 *     production_variance: string | null,
 *     production_volume_variance: string | null
 *   }>,
 *   meta: Object,
 *   summary: Object
 * }>}
 */
export const fetchAreaActivitiesByArea = async ({ start_date, end_date, plan_version = null, area_id = null } = {}) => {
  try {
    const params = new URLSearchParams();
    if (start_date) params.append('start_date', start_date);
    if (end_date) params.append('end_date', end_date);
    if (plan_version !== null && plan_version !== undefined) params.append('plan_version', plan_version);
    if (area_id) params.append('area_id', area_id);

    const response = await fetch(`${AREA_PLANNER_BASE}/activities-by-area?${params.toString()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'API returned unsuccessful response');
    }
    return data;
  } catch (error) {
    console.error('Error fetching area activities by area:', error);
    throw error;
  }
};

/**
 * Fetch the live area log: what actually ran in each area, from
 * equipment_log_books, on the Nucleus Plus area set.
 *
 * The area-side counterpart of fetchPlanningByArea. Returns the activities and
 * the area rows together, so the timeline can show quiet areas too.
 *
 * @param {string|Date} [date] - DD-MM-YYYY / YYYY-MM-DD / Date. Omit for the last 24h.
 * @param {string} [areaId] - Optional, filter to one area
 *
 * @returns {Promise<{
 *   success: boolean,
 *   activities: Array<{
 *     id: number,
 *     area_id: string,
 *     area_name: string,
 *     area_sequence: number | null,
 *     activity_type: string,
 *     batch_number: string | null,
 *     product_name: string | null,
 *     cleaning_type: string | null,
 *     start_time: string,
 *     end_time: string | null
 *   }>,
 *   areas: Array<{ id: number, area_id: string, area_name: string, sequence: number | null }>,
 *   meta: Object
 * }>}
 */
export const fetchAreaLog = async (date = null, areaId = null) => {
  try {
    const params = new URLSearchParams();
    if (date) {
      params.append('date', date instanceof Date ? date.toISOString().split('T')[0] : date);
    }
    if (areaId) params.append('area_id', areaId);

    const query = params.toString();
    const response = await fetch(`${AREA_PLANNER_BASE}/area-log${query ? `?${query}` : ''}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'API returned unsuccessful response');
    }
    return data;
  } catch (error) {
    console.error('Error fetching area log:', error);
    throw error;
  }
};
